import React, { useContext, useEffect, useState } from 'react'
import api from '../../utils/api'
import { useAuth } from '../../context/AuthContext'
import { CaseDetailContext } from './CaseDetailContext'

/**
 * Case Notes — comment-thread style. Replaces the old TipTap editor
 * with a chronological feed of notes, each posted by the case manager.
 *
 * Layout matches the screenshot the user shared:
 *   Header (gray-50): "Notes"
 *   Each comment row: avatar + author + relative time + text body,
 *     with a thin vertical connector between consecutive entries.
 *   Bottom: textarea + "Comment" button.
 *
 * These comments are INTERNAL to case managers — they are stored in
 * `cases.case_manager_comments` (a CM-only JSONB column) and are never
 * shown on the Scale/mediator side. They must NOT be written to
 * `cases.mediator_notes`, which is the mediator's own field that Scale
 * renders verbatim — see caseController.getCmComments.
 */

// "2 mins ago" / "3 hours ago" / "Apr 12" — same flavor as the screenshot.
function relativeTime(iso) {
    if (!iso) return ''
    const ts = new Date(iso).getTime()
    if (Number.isNaN(ts)) return ''
    const diff = Math.max(0, Date.now() - ts)
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins} ${mins === 1 ? 'min' : 'mins'} ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function initialsOf(name) {
    if (!name) return 'CM'
    return String(name)
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
}

export default function MediatorNotes({ onToast }) {
    const { caseData } = useContext(CaseDetailContext)
    const { user, caseManager } = useAuth()
    const [comments, setComments] = useState([])
    const [text, setText] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [loading, setLoading] = useState(false)
    // Inline-edit state. `editingId` is the comment id currently in edit
    // mode; `editingText` is the in-progress draft. Only one comment can
    // be edited at a time — opening another edit cancels the prior draft.
    const [editingId, setEditingId] = useState(null)
    const [editingText, setEditingText] = useState('')
    const [savingEdit, setSavingEdit] = useState(false)

    const caseId = caseData?.id

    // Fetch the comments thread once we have the case id, and again any
    // time the case context changes (e.g. user navigates between cases).
    useEffect(() => {
        if (!caseId) return undefined
        let cancelled = false
        setLoading(true)
        api.get(`/cases/${caseId}/notes/comments`)
            .then((res) => {
                if (cancelled) return
                const list = Array.isArray(res?.data?.data) ? res.data.data : []
                setComments(list)
            })
            .catch(() => { if (!cancelled) setComments([]) })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [caseId])

    const handlePost = async () => {
        const trimmed = text.trim()
        if (!trimmed || submitting || !caseId) return
        try {
            setSubmitting(true)
            const author = (user?.firstName || caseManager?.firstName)
                ? `${user?.firstName || caseManager?.firstName || ''} ${user?.lastName || caseManager?.lastName || ''}`.trim()
                : 'Case Manager'
            const res = await api.post(`/cases/${caseId}/notes/comments`, { author, text: trimmed })
            const list = Array.isArray(res?.data?.data) ? res.data.data : []
            setComments(list)
            setText('')
        } catch (err) {
            onToast?.({ type: 'error', message: err?.response?.data?.message || 'Failed to post comment' })
        } finally {
            setSubmitting(false)
        }
    }

    const startEdit = (comment) => {
        setEditingId(comment.id)
        setEditingText(comment.text || '')
    }

    const cancelEdit = () => {
        setEditingId(null)
        setEditingText('')
    }

    const saveEdit = async () => {
        const trimmed = editingText.trim()
        if (!trimmed || savingEdit || editingId == null || !caseId) return
        try {
            setSavingEdit(true)
            const res = await api.put(`/cases/${caseId}/notes/comments/${editingId}`, { text: trimmed })
            const list = Array.isArray(res?.data?.data) ? res.data.data : []
            setComments(list)
            cancelEdit()
        } catch (err) {
            onToast?.({ type: 'error', message: err?.response?.data?.message || 'Failed to update comment' })
        } finally {
            setSavingEdit(false)
        }
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Header — solid gray band with "Notes" title only,
                matching the screenshot. */}
            <div className="bg-gray-50 px-6 py-5 border-b border-gray-200">
                <div className="text-lg font-semibold text-gray-900">Notes</div>
            </div>

            {/* Comments thread */}
            <div className="px-6 pt-6">
                {loading && (
                    <div className="text-xs text-gray-500 mb-4">Loading…</div>
                )}
                {!loading && comments.length === 0 && (
                    <div className="text-sm text-gray-500 mb-4">No notes yet. Add the first one below.</div>
                )}
                <ul className="relative">
                    {comments.map((c, idx) => {
                        const initials = c.initials || initialsOf(c.author || c.name)
                        const author = c.author || c.name || 'Case Manager'
                        const when = relativeTime(c.created_at)
                        const isLast = idx === comments.length - 1
                        const isEditing = editingId != null && String(editingId) === String(c.id)
                        return (
                            <li key={c.id || idx} className="group relative flex gap-3 pb-6">
                                {/* Vertical connector to the next item, like the screenshot. */}
                                {!isLast && (
                                    <span className="absolute left-[18px] top-9 bottom-0 w-px bg-gray-200" aria-hidden="true" />
                                )}
                                <div className="shrink-0 w-9 h-9 rounded-full bg-[#d8b8c4] flex items-center justify-center overflow-hidden">
                                    <span className="text-[11px] font-semibold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                                        {initials}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                                            {author}
                                        </span>
                                        <span className="text-xs text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
                                            {when}
                                        </span>
                                        {c.updated_at && (
                                            <span className="text-xs text-gray-400 italic" style={{ fontFamily: 'Inter, sans-serif' }}>
                                                (edited)
                                            </span>
                                        )}
                                        {/* Edit affordance — same mint pencil chip used on
                                            the Mediator Fee editor. Sits inline after the
                                            relative-time text, visible on hover. */}
                                        {!isEditing && (
                                            <button
                                                type="button"
                                                onClick={() => startEdit(c)}
                                                title="Edit note"
                                                aria-label="Edit note"
                                                className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 6,
                                                    background: '#e8fdf5',
                                                    border: '1px solid #b7ebdf',
                                                    color: '#115e59',
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                    <path
                                                        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487zm0 0L19.5 7.125"
                                                        stroke="currentColor"
                                                        strokeWidth="1.8"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                    </div>

                                    {!isEditing ? (
                                        <p
                                            className="mt-1 text-sm text-gray-700 whitespace-pre-wrap"
                                            style={{ fontFamily: 'Inter, sans-serif', lineHeight: 1.55 }}
                                        >
                                            {c.text}
                                        </p>
                                    ) : (
                                        <div className="mt-2">
                                            <textarea
                                                autoFocus
                                                value={editingText}
                                                onChange={(e) => setEditingText(e.target.value)}
                                                rows={3}
                                                className="block w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#115E59] focus:border-[#115E59]"
                                                style={{ fontFamily: 'Inter, sans-serif' }}
                                            />
                                            <div className="mt-2 flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={saveEdit}
                                                    disabled={savingEdit || !editingText.trim() || editingText === (c.text || '')}
                                                    className="px-3 py-1.5 text-xs font-medium text-white rounded-md disabled:opacity-60"
                                                    style={{ backgroundColor: '#115E59', fontFamily: 'Inter, sans-serif' }}
                                                >
                                                    {savingEdit ? 'Saving…' : 'Save'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelEdit}
                                                    disabled={savingEdit}
                                                    className="px-3 py-1.5 text-xs font-medium text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                                                    style={{ fontFamily: 'Inter, sans-serif' }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>

            {/* Composer — textarea + Comment button, inside a rounded outline. */}
            <div className="px-6 pb-6">
                <div className="rounded-xl border border-gray-300 bg-white">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder=""
                        rows={4}
                        className="block w-full resize-none px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none rounded-t-xl"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                    <div className="flex justify-start px-4 pb-3">
                        <button
                            type="button"
                            onClick={handlePost}
                            disabled={submitting || !text.trim()}
                            className="px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-60"
                            style={{ backgroundColor: '#115E59', fontFamily: 'Inter, sans-serif' }}
                        >
                            {submitting ? 'Posting…' : 'Comment'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
